import { ClientError, GraphQLClient } from 'graphql-request';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';

import { ACCESS_TOKEN_KEY } from '@/constants';
import { getHTTPHeaders, GRAPHQL_URL } from '@shared/auth/authentication.hooks';
import { clearTokens } from '@shared/auth/tokens';
import { refreshTokenSingleFlight } from '@shared/auth/refresh';
import { reportQueryError } from '@shared/observability';
import { translate } from '@shared/i18n';

/**
 * The single authenticated GraphQL entry point for the app.
 *
 * Replaces the old `QueryRequest` class (`shared/query-client/QueryClient.ts`),
 * which every view hook reached for via `AppContext`. Two things changed in the
 * move, both deliberate:
 *
 * 1. **No mutable client, no `rebuild` callback.** `QueryRequest` held a long-lived
 *    `GraphQLClient` whose `Authorization` header had to be patched after a token
 *    refresh, plus a `rebuild` hook that `App.tsx` reassigned *during render* to
 *    rebuild the React-held client. Here headers are read fresh from storage on
 *    every call, so a refreshed token is picked up by the next request with no
 *    coordination at all and that whole mechanism disappears.
 * 2. **One implementation instead of two.** `query()` and `queryWithClient()` had
 *    near-identical 401 handling that had already drifted apart slightly; callers
 *    that needed anonymous or admin headers hand-built their own `GraphQLClient`
 *    and passed it in. That is now the `withAuth` flag.
 *
 * The 401-refresh-and-retry behaviour was ported as-is from `QueryRequest`,
 * including a known defect (see `onUnauthenticated` below), so the topic-3
 * extraction stayed behaviour-neutral. Topic 4 (auth) owns refresh
 * correctness and has since fixed the refresh call itself to be
 * single-flight (`services/auth/refresh.ts`) — the whole-cache invalidation
 * on 401 and the hard `clearTokens('/')` redirect on refresh failure are
 * still open, see `improvements/04-authentication-security.md`.
 *
 * Two call shapes (topic 5, 2026-08-13): pass a codegen'd `TypedDocumentNode`
 * (from `services/api/gql`'s `graphql()` tag) and both `T`/`V` are inferred
 * automatically — no explicit generic, no hand-maintained response type.
 * The plain-`string` overload still exists for the many call sites not yet
 * migrated off hand-rolled query strings; new code should prefer the typed
 * one.
 */
export async function graphqlFetch<
	T,
	V extends Record<string, unknown> | undefined,
>(
	document: TypedDocumentNode<T, V>,
	variables?: V,
	options?: GraphqlFetchOptions,
): Promise<T>;
export async function graphqlFetch<
	T,
	// Defaulted to the permissive shape, not `undefined`: callers routinely pin
	// only the result type (`graphqlFetch<PreviewQuery>(doc, vars)`), and with
	// `undefined` as the default TypeScript would then reject every `vars`
	// argument rather than inferring it.
	V extends Record<string, unknown> | undefined =
		Record<string, unknown> | undefined,
>(document: string, variables?: V, options?: GraphqlFetchOptions): Promise<T>;
export async function graphqlFetch<
	T,
	V extends Record<string, unknown> | undefined,
>(
	document: string | TypedDocumentNode<T, V>,
	variables?: V,
	options: GraphqlFetchOptions = {},
): Promise<T> {
	const { withAuth = true, signal } = options;

	const run = () =>
		new GraphQLClient(GRAPHQL_URL, {
			headers: getHTTPHeaders(withAuth),
		}).request<T>({
			document: document as string,
			variables: variables as Record<string, unknown> | undefined,
			signal,
		});

	try {
		return await run();
	} catch (error) {
		// A cancelled request (the reader left while it was in flight) is
		// neither a failure worth reporting nor a 401 worth refreshing for --
		// hand the abort straight back to whoever cancelled it.
		if (signal?.aborted) {
			throw error;
		}
		if (!(error instanceof ClientError)) {
			// Network error, malformed response, etc. -- not a 401, nothing to
			// retry. Reported here rather than at every call site.
			reportQueryError({ document, variables, error });
			throw error;
		}
		if (!isUnauthenticated(error)) {
			// A real GraphQL error (validation, permission, server) that
			// retrying with a refreshed token can't fix.
			reportQueryError({ document, variables, error });
			throw error;
		}

		// PORTED AS-IS / KNOWN DEFECT: this invalidates *every* query in the
		// cache on any single 401, not just the one that failed, and it does so
		// before the refresh has even been attempted. Preserved deliberately to
		// keep this extraction behaviour-neutral; topic 4 owns the fix.
		onUnauthenticated?.();

		try {
			const refreshed = await refreshTokenSingleFlight();
			if (!refreshed) {
				throw new Error(translate('common.sessionExpired'));
			}
			if (!localStorage.getItem(ACCESS_TOKEN_KEY)) {
				throw new Error(translate('common.sessionExpired'));
			}
			// No header patching needed: `run()` re-reads headers from storage.
			return await run();
		} catch {
			// Not reported as a `query_error` here on purpose: a session
			// genuinely expiring isn't a fact about this operation, it's an
			// auth-refresh failure -- that's `auth_refresh_exhausted`, owned
			// by the refresh.ts call site (Section 16's next item), not this
			// one. Reporting it here too would double-classify the same
			// failure and add noise for every logged-out user.
			clearTokens('/');
			return Promise.reject(
				new Error('Session expired. Please log in again.'),
			);
		}
	}
}

export interface GraphqlFetchOptions {
	/**
	 * Send the `Authorization` header. `false` reproduces the call sites that
	 * used to hand-build an anonymous `GraphQLClient` via `getHTTPHeaders(false)`
	 * and pass it to `queryWithClient`. Defaults to `true`.
	 */
	withAuth?: boolean;
	/**
	 * Aborts the request -- pass TanStack Query's `queryFn` `signal` so a
	 * cancelled query (`queryClient.cancelQueries`, or its last observer
	 * unmounting mid-fetch) cancels the HTTP request too, not just stops
	 * waiting for it.
	 */
	signal?: AbortSignal;
}

/**
 * Set once at app start so this module can invalidate the query cache on a 401
 * without importing the QueryClient (which would re-create the services →
 * query-client → views/templates import cycle this extraction is untangling).
 */
let onUnauthenticated: (() => void) | undefined;

export function setUnauthenticatedHandler(handler: () => void): void {
	onUnauthenticated = handler;
}

function isUnauthenticated(error: ClientError): boolean {
	return !!error.response.errors?.some(
		(err) =>
			err.message.includes('jwt expired') ||
			err.extensions?.code === 'UNAUTHENTICATED',
	);
}
